import React, { useState, useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../../shared/CustomButton';
import Spinner from '../../../shared/Spinner';
import TextInput from '../../../shared/TextInput';
import DropdownField from '../../../shared/DropdownField';
import { paymentMethods, cardLabels, getCardOptions, getCardCost, widthdrawPaymentMethods } from '../../../constants/VisitsConstants';
import { FaCheckDouble, FaTrash } from 'react-icons/fa';
import { fetchData } from '../../../helpers/fetchData';
import CheckPermission from '../../../hooks/CheckPermission';
import toast from 'react-hot-toast';
import { practionerDepartmentOptions } from '../../../constants/UserConstants';
import moment from 'moment';
import DatePicker from "../../../shared/DatePicker";

const WithdrawDepositForm = ({
  selectedPatient,
  setOpenWithdrawForm,
  onWithdrawSuccess,
  withOutSlot = false,
  state,
  prevPath,
  onPrint
}) => {
  const { hasPermission } = CheckPermission();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [fetchingBalance, setFetchingBalance] = useState(false);
  const [cardOptions, setCardOptions] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!selectedPatient?.patient_id) return;

      setFetchingBalance(true);
      try {
        const response = await fetchData('POST', 'v3/deposit/getdeposit', {
          patient_id: selectedPatient?.patient_id
        });
        if (response?.balance !== undefined) {
          setAvailableBalance(response.balance);
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
        toast.error(error?.message || t('Failed to fetch deposit balance'));
      } finally {
        setFetchingBalance(false);
      }
    };

    fetchBalance();
  }, [selectedPatient, t]);

  const initialValues = useMemo(() => ({
    patient_id: selectedPatient?.patient_id || '',
    department_id: '',
    withdraw_date: new Date(),
    withdraw_amount: '',
    withdraw_amount_without_vat: '',
    total_vat: 0,
    withdrawer_name: selectedPatient?.full_name || '',
    withdrawer_contact: selectedPatient?.phone || '',
    withdraw_remarks: '',
    payment_option: 'cash',
    cardType: 'Mada',
    reference: '',
    cash: null,
    bank: null,
    Mada: null,
    cards: [{ cardType: 'Mada', amount: '' }]
  }), [selectedPatient]);

  const validationSchema = Yup.object()
    .shape({
      department_id: Yup.string().required(t('Department is required')),
      withdraw_date: Yup.date()
        .required(t('Withdraw date is required'))
        .max(new Date(), t('Withdraw date cannot be in the future')),
      withdraw_amount: Yup.number()
        .required(t('Withdraw amount is required'))
        .min(0, t('Must be positive or zero'))
        .max(availableBalance, t('Cannot withdraw more than available balance')),
      withdrawer_name: Yup.string().required(t('Withdrawer name is required')),
      withdraw_remarks: Yup.string(),
      payment_option: Yup.string().required(t('Payment option is required')),
      reference: Yup.string(),
      cash: Yup.number()
        .nullable()
        .when('payment_option', {
          is: 'multi_payment',
          then: schema => schema.required(t('Cash amount is required')).min(0, t('Must be positive or zero')),
          otherwise: schema => schema.nullable()
        }),
      bank: Yup.number()
        .nullable()
        .when('payment_option', {
          is: 'multi_payment',
          then: schema => schema.required(t('Bank amount is required')).min(0, t('Must be positive or zero')),
          otherwise: schema => schema.nullable()
        })
    })
    .test('payment-total-validation', t('Payment methods total cannot exceed withdraw amount'), function (values) {
      if (values.payment_option === 'multi_payment') {
        const withdrawAmount = parseFloat(values.withdraw_amount) || 0;
        const cash = parseFloat(values.cash) || 0;
        const bank = parseFloat(values.bank) || 0;
        const cardsTotal = values.cards?.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0) || 0;
        const total = cash + bank + cardsTotal;

        if (Math.abs(total - withdrawAmount) > 0.05) {
          return this.createError({
            path: 'cash',
            message: t('Total payment must match the withdraw amount')
          });
        }
      }
      return true;
    });

  const handleSubmit = async (values) => {
    if (!hasPermission('deposit', 'update')) return;
    if (values?.payment_option === "card" || values?.payment_option === "multi_payment") {
      if (values?.payment_option === "card") {
        const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
          Array.isArray(group.options) && group.options.some(opt => opt.value === values.cardType || opt.label === values.cardType)
        );
        if (!values.cardType || !isValid) {
          toast.error(t("Please Select Card Type"));
          return;
        }
      } else if (values?.payment_option === "multi_payment") {
        const cardPayments = values.cards?.filter(card => Number(card.amount) > 0) || [];
        for (const card of cardPayments) {
          const isValid = Array.isArray(cardOptions) && cardOptions.some(group =>
            Array.isArray(group.options) && group.options.some(opt => opt.value === card.cardType || opt.label === card.cardType)
          );
          if (!card.cardType || !isValid) {
            toast.error(t("Please Select Card Type for all card payments"));
            return;
          }
        }
      }
    }

    try {
      let paymentDetail = [];

      if (values.payment_option === 'multi_payment') {
        if (values.cash > 0) {
          paymentDetail.push({
            payment_method: 'cash',
            amount: parseFloat(values.cash)
          });
        }
        if (values.bank > 0) {
          paymentDetail.push({
            payment_method: 'bank',
            amount: parseFloat(values.bank)
          });
        }
        if (values.cards && values.cards.length > 0) {
          values.cards.forEach(card => {
            if (card.amount > 0) {
              paymentDetail.push({
                payment_method: card.cardType,
                amount: parseFloat(card.amount),
                card_cost: getCardCost(cardOptions, card.cardType, card.amount)
              });
            }
          });
        }
      } else {
        paymentDetail = [
          {
            payment_method: values.payment_option === 'cash' ? 'Cash' : values.payment_option,
            amount: parseFloat(values.withdraw_amount),
            ...(values.payment_option === 'card' && {
              card_cost: getCardCost(cardOptions, values.cardType, values.withdraw_amount)
            })
          }
        ];
      }

      const payload = {
        patient_id: values.patient_id,
        department_id: values.department_id,
        withdraw_detail: {
          withdraw_date: moment(values?.withdraw_date)
            .hour(moment().hour())
            .minute(moment().minute())
            .second(moment().second())
            .format('YYYY-MM-DD HH:mm:ss'),
          reference: values.reference,
          withdraw_amount: parseFloat(values.withdraw_amount_without_vat),
          total_amount_with_vat: parseFloat(values.withdraw_amount),
          total_vat: parseFloat(values.total_vat),
          withdrawer_name: values.withdrawer_name,
          withdrawer_contact: values.withdrawer_contact,
          withdraw_remarks: values.withdraw_remarks,
          payment_option: values.payment_option,
          payment_detail: paymentDetail
        }
      };

      if (payload?.withdraw_detail?.payment_option == 'multi_payment') {
        const totalPayment = payload.withdraw_detail.payment_detail.reduce((sum, method) => sum + method.amount, 0);
        if (totalPayment !== parseFloat(values.withdraw_amount)) {
          toast.error(t('Total payment methods amount must equal withdraw amount'));
          return;
        }
      }
      setLoading(true);
      const response = await fetchData('POST', '/v3/deposit/withdrawDeposit', payload);

      if (response) {
        toast.success(response?.message || t('Deposit withdrawn successfully'));
        if (onWithdrawSuccess) {
          onWithdrawSuccess();
        }

        if (response?.invoice_number) {
          if (onPrint) {
            onPrint(response.invoice_number, true);
            setOpenWithdrawForm(false);
          } else {
            try {
              navigate(`/print-invoice/${response?.invoice_number}`, {
                state: {
                  ...state,
                  invoiceNumber: response?.invoice_number,
                  withdraw: true,
                  navigattion: prevPath || (withOutSlot ? '/create-visit-without-slot' : '/create-visit'),
                  patient: selectedPatient,
                  deposit: false
                }
              });
            } catch (invoiceError) {
              console.error('Error creating invoice:', invoiceError);
              toast.error(invoiceError?.message || t('Failed to create invoice'));
              setOpenWithdrawForm(false);
            }
          }
        } else {
          setOpenWithdrawForm(false);
        }
      }
    } catch (error) {
      console.error('Error withdrawing deposit:', error);
      toast.error(error?.message || t('Failed to withdraw deposit'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetchData('POST', '/v3/department/getList', {});
      if (response?.data) setDepartments(practionerDepartmentOptions(response.data));
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  return (
    <div className='p-4'>
      {fetchingBalance ? (
        <div className='flex justify-center items-center py-8'>
          <Spinner />
          <span className='ml-2'>{t('Fetching balance')}...</span>
        </div>
      ) : (
        <>
          <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <div className='flex justify-between items-center'>
              <span className='text-sm font-medium text-gray-700'>{t('Available Balance')}:</span>
              <span className='text-lg font-bold text-blue-600'>{availableBalance.toFixed(2)} SAR</span>
            </div>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <DropdownField
                    filter
                    label={t('Select Department')}
                    options={departments}
                    name='department_id'
                    optionLabel='label'
                    optionValue='value'
                    placeholder={t('Select Department')}
                    className='w-full'
                    onChange={e => {
                      const value = e.value;

                      setFieldValue('department_id', e.value);
                      if (value) {
                        const payload = {
                          department: {
                            id: value
                          }
                        };
                        getCardOptions(setCardOptions, payload);
                      }
                    }}
                  />
                  <TextInput
                    name='withdraw_amount'
                    label={t('Withdraw Amount')}
                    type='number'
                    placeholder={t('Enter amount to withdraw')}
                    required
                    step='any'
                    onChange={e => {
                      const amount = parseFloat(e.target.value) || 0;
                      setFieldValue('withdraw_amount', e.target.value);
                      const vatRate = 0.15;
                      const saudiPatient = selectedPatient?.nationality?.toLowerCase()==="saudi"
                      const totalVat = saudiPatient ? 0 : amount * vatRate;
                      setFieldValue('total_vat', totalVat.toFixed(2));
                      setFieldValue('withdraw_amount_without_vat', (amount - totalVat).toFixed(2));
                    }}
                  />
                  <DatePicker
                    label={t('Withdraw Date')}
                    name='withdraw_date'
                    dateFormat="dd/mm/yy"
                    placeholder={t('Select withdraw date')}
                    maxDate={new Date()}
                  />
                  <TextInput name='patient_id' label={t('Patient ID')} disabled value={values.patient_id} />

                  <TextInput name='reference' label={t('Reference')} placeholder={t('Enter reference (optional)')} />

                  <TextInput
                    name='withdrawer_name'
                    label={t('Withdrawer Name')}
                    placeholder={t('Enter withdrawer name')}
                    required
                  />

                  {/* <TextInput
                    name="withdrawer_contact"
                    label={t("Withdrawer Contact")}
                    placeholder={t("Enter contact number")}
                    required
                  /> */}

                  <DropdownField
                    name='payment_option'
                    label={t('Payment Method')}
                    options={widthdrawPaymentMethods(t)}
                    value={values.payment_option}
                    onChange={e => {
                      const method = e.value;
                      const currentCardKey = values.cardType || 'Mada';

                      setFieldValue('payment_option', method);
                      if (method === 'multi_payment') {
                        setFieldValue('cash', values.cash ?? 0);
                        setFieldValue('bank', values.bank ?? 0);
                      } else {
                        setFieldValue('cash', null);
                        setFieldValue('bank', null);
                      }
                    }}
                    required
                  />

                  {(values.payment_option === 'card' || values.payment_option === 'multi_payment') && (
                    <DropdownField
                      name='cardType'
                      label={values.payment_option === 'multi_payment' ? t('Add Card') : t('Card Type')}
                      options={cardOptions}
                      placeholder={t('SelectCardType')}
                      value={values.payment_option === 'multi_payment' ? null : values.cardType}
                      onChange={e => {
                        if (values.payment_option === 'multi_payment') {
                          if (e.value) {
                            const newCards = [...(values.cards || []), { cardType: e.value, amount: '' }];
                            setFieldValue('cards', newCards);
                            setTimeout(() => setFieldValue('cardType', null), 0);
                          }
                        } else {
                          setFieldValue('cardType', e.value);
                        }
                      }}
                    />
                  )}

                  {values.payment_option === 'multi_payment' && (
                    <>
                      <TextInput
                        name='cash'
                        label={t('Cash')}
                        placeholder={t('EnterCashAmount')}
                        type='number'
                        step='any'
                      />
                      <TextInput
                        name='bank'
                        label={t('Bank')}
                        placeholder={t('EnterBankAmount')}
                        type='number'
                        step='any'
                      />

                      {/* {values.cards &&
                        values.cards.map((card, index) => (
                          <div key={index} className='flex items-end gap-2'>
                            <div className='flex-1'>
                              <TextInput
                                name={`cards.${index}.amount`}
                                label={cardLabels(t)[card.cardType] || card.cardType}
                                placeholder={t('Amount')}
                                type='number'
                                step='any'
                              />
                            </div>
                            <div className='mb-1'>
                              <button
                                type='button'
                                onClick={() => {
                                  const newCards = values.cards.filter((_, i) => i !== index);
                                  setFieldValue('cards', newCards);
                                }}
                                className='text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors'
                                title={t('Remove')}
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          </div>
                        ))} */}
                    </>
                  )}

                  <TextInput
                    name='withdraw_remarks'
                    label={t('Remarks')}
                    placeholder={t('Enter remarks (optional)')}
                    className='md:col-span-2'
                  />
                </div>
                <div className='w-full flex items-center justify-end'>
                  <div className='bg-gray-100 p-2 rounded-md mt-3 w-[300px] flex flex-col gap-3'>
                    <div className='flex justify-between items-center my-1'>
                      <h6 className='text-14-gray !font-[600]'>{t('Withdraw Amount Without Vat')} </h6>
                      <p className='text-14-dark'>{values.withdraw_amount_without_vat || 0}</p>
                    </div>
                    <div className='flex justify-between items-center my-1'>
                      <h6 className='text-14-gray !font-[600]'>{t('Total Vat')} </h6>
                      <p className='text-14-dark'>{values.total_vat || 0}</p>
                    </div>
                    <div className='flex justify-between items-center my-1'>
                      <h6 className='text-14-gray !font-[600]'>{t('Total Withdraw Amount With Vat')} </h6>
                      <p className='text-14-dark'>{values.withdraw_amount || 0}</p>
                    </div>
                  </div>
                </div>

                <div className='flex justify-end gap-3 mt-6'>
                  <CustomButton
                    buttonType='reset'
                    label={t('Cancel')}
                    onClick={() => setOpenWithdrawForm(false)}
                    disabled={loading}
                  />
                  {hasPermission('deposit', 'update') && <CustomButton
                    buttonType='success'
                    type='submit'
                    icon={loading ? <Spinner /> : <FaCheckDouble />}
                    label={loading ? t('Processing') : t('Withdraw')}
                    disabled={loading}
                  />}
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};

export default WithdrawDepositForm;
